// Shared sign-in / create-account form.
//
// This is the whole of the auth experience except the container it sits in:
// LoginModal wraps it in a Bootstrap modal, SignupPage renders it on a route of
// its own so marketing campaigns have a URL to link to. Both mount the same
// component, so a field added here shows up in both places and the two can
// never drift apart.
//
// `mode` is controlled by the parent because the containers need to seed and
// react to it differently — the modal resets to its `initialMode` every time it
// opens, the page starts on 'signup' and updates its heading when the visitor
// switches.
import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Alert, ProgressBar, Spinner } from 'react-bootstrap';
import { useAuth } from '../../hooks/useAuth';
import { SMS_CONSENT_TEXT, SMS_CONSENT_VERSION } from './smsConsent';
import {
  validateEmail, validatePassword, validatePhone, validateZip, validateRequired, getPasswordStrength,
  formatPhoneInput, formatZipInput, formatEmailInput
} from '../../utils/validation';
import SocialAuthButtons, { socialAuthAvailable } from './SocialAuthButtons';
import { blobStorageService } from '../../services/blobStorageService';
import './AuthForm.css';

// Long enough for the success alert to register before the container takes the
// visitor somewhere else.
const SUCCESS_DISMISS_MS = 1000;

// Typed fields only ever hold what they ask for: a phone box takes a phone
// number, a ZIP box a ZIP, an email box no spaces.
const FIELD_FILTERS = {
  email: formatEmailInput,
  phoneNumber: formatPhoneInput,
  zipCode: formatZipInput
};

const EMPTY_FORM = {
  email: '',
  password: '',
  confirmPassword: '',
  username: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  zipCode: '',
  need1: '',
  need2: '',
  need3: '',
  profilePicture: null,
  identity: '',
  acceptTerms: false,
  newsletterOptIn: false,
  smsConsent: false
};

const AuthForm = ({ mode, onModeChange, onSuccess }) => {
  const { login, register } = useAuth();

  // Form state
  const [formData, setFormData] = useState(EMPTY_FORM);

  // Validation errors
  const [errors, setErrors] = useState({});

  // Submit state
  const [loading, setLoading] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');

  // Show/hide password
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password strength
  const passwordStrength = mode === 'signup' ? getPasswordStrength(formData.password) : 0;

  /* The success hand-off is deferred, and on the standalone page it navigates
     away. Without this the timer could fire against an unmounted component. */
  const successTimer = useRef(null);
  useEffect(() => () => clearTimeout(successTimer.current), []);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    const filter = FIELD_FILTERS[name];

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : filter ? filter(value) : value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!validateRequired(formData.email)) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Password validation
    if (!validateRequired(formData.password)) {
      newErrors.password = 'Password is required';
    } else if (mode === 'signup') {
      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        newErrors.password = passwordValidation.errors[0];
      }
    }

    if (mode === 'signup') {
      // Confirm password
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }

      // First name
      if (!validateRequired(formData.firstName)) {
        newErrors.firstName = 'First name is required';
      }

      // Last name
      if (!validateRequired(formData.lastName)) {
        newErrors.lastName = 'Last name is required';
      }

      // Phone (required)
      if (!validateRequired(formData.phoneNumber)) {
        newErrors.phoneNumber = 'Phone number is required';
      } else if (!validatePhone(formData.phoneNumber)) {
        newErrors.phoneNumber = 'Invalid phone number';
      }

      // Zip (optional, but if given it must be a full ZIP)
      if (formData.zipCode && !validateZip(formData.zipCode)) {
        newErrors.zipCode = 'Zip code must be 5 digits';
      }

      // Identity (required)
      if (!validateRequired(formData.identity)) {
        newErrors.identity = 'Please select how you identify yourself';
      }

      // Terms of service
      if (!formData.acceptTerms) {
        newErrors.acceptTerms = 'You must accept the terms of service';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSubmitError('');
    setSubmitSuccess('');

    try {
      let result;

      if (mode === 'login') {
        result = await login(formData.email, formData.password);
      } else {
        let profilePictureUrl = null;

        // Upload profile picture to blob storage if provided
        if (formData.profilePicture) {
          try {
            setUploadingPicture(true);
            // Generate temporary userId (use email as identifier before user is created)
            const tempUserId = formData.email.split('@')[0];
            const uploadResult = await blobStorageService.uploadProfilePicture(
              formData.profilePicture,
              tempUserId
            );
            profilePictureUrl = uploadResult.url;
          } catch (uploadError) {
            console.error('Error uploading profile picture:', uploadError);
            setSubmitError('Account created but failed to upload profile picture. You can add it later from your profile.');
          } finally {
            setUploadingPicture(false);
          }
        }

        result = await register({
          email: formData.email,
          password: formData.password,
          username: formData.username || formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: formData.phoneNumber,
          zipCode: formData.zipCode,
          need1: formData.need1,
          need2: formData.need2,
          need3: formData.need3,
          profilePicture: profilePictureUrl,
          identity: formData.identity,
          termsAccepted: formData.acceptTerms,
          newsletterOptIn: formData.newsletterOptIn,
          // Sent with the wording's version, so the server records exactly
          // what they agreed to (or that they saw it and left it unchecked).
          smsConsent: formData.smsConsent,
          smsConsentVersion: SMS_CONSENT_VERSION,
          smsConsentSource: window.location.pathname
        });
      }

      if (result.success) {
        const completedMode = mode;
        setSubmitSuccess(completedMode === 'login' ? 'Login successful!' : 'Account created successfully!');

        // Reset form
        setFormData(EMPTY_FORM);

        // Hand back to the container after the success message has been seen.
        successTimer.current = setTimeout(() => {
          onSuccess(completedMode);
        }, SUCCESS_DISMISS_MS);
      } else {
        setSubmitError(result.error || 'An error occurred');
      }
    } catch (error) {
      setSubmitError(error.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleModeSwitch = () => {
    onModeChange(mode === 'login' ? 'signup' : 'login');
    setErrors({});
    setSubmitError('');
    setSubmitSuccess('');
  };

  const handleForgotPassword = () => {
    // TODO: Implement password reset modal
  };

  return (
    <div className="auth-form">
      {submitError && <Alert variant="danger">{submitError}</Alert>}
      {submitSuccess && <Alert variant="success">{submitSuccess}</Alert>}

      {/* Social sign-in lands the visitor straight in — no success alert to
          read, so it hands back immediately. */}
      <SocialAuthButtons onSuccess={() => onSuccess(mode)} />

      {socialAuthAvailable && (
        <div className="or-divider my-4">
          <span>OR</span>
        </div>
      )}

      <Form onSubmit={handleSubmit}>
        {/* Email */}
        <Form.Group className="mb-3">
          <Form.Label>Email Address *</Form.Label>
          <Form.Control
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            isInvalid={!!errors.email}
            placeholder="Enter your email"
            autoComplete="email"
          />
          <Form.Control.Feedback type="invalid">
            {errors.email}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Password */}
        <Form.Group className="mb-3">
          <Form.Label>Password *</Form.Label>
          <div className="password-input-wrapper">
            <Form.Control
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              isInvalid={!!errors.password}
              placeholder={mode === 'signup' ? 'Create a strong password' : 'Enter your password'}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
          <Form.Control.Feedback type="invalid" style={{ display: errors.password ? 'block' : 'none' }}>
            {errors.password}
          </Form.Control.Feedback>

          {mode === 'signup' && formData.password && (
            <div className="password-strength mt-2">
              <small>Password strength:</small>
              <ProgressBar
                now={passwordStrength}
                variant={passwordStrength < 50 ? 'danger' : passwordStrength < 80 ? 'warning' : 'success'}
                className="mt-1"
              />
            </div>
          )}
        </Form.Group>

        {mode === 'signup' && (
          <>
            {/* Confirm Password */}
            <Form.Group className="mb-3">
              <Form.Label>Confirm Password *</Form.Label>
              <div className="password-input-wrapper">
                <Form.Control
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  isInvalid={!!errors.confirmPassword}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              <Form.Control.Feedback type="invalid">
                {errors.confirmPassword}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Username */}
            <Form.Group className="mb-3">
              <Form.Label>Username (optional)</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="Choose a username (defaults to email)"
              />
              <Form.Text className="text-muted">
                If not provided, your email will be used as your username
              </Form.Text>
            </Form.Group>

            {/* Name fields */}
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>First Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    isInvalid={!!errors.firstName}
                    placeholder="First name"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.firstName}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Last Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    isInvalid={!!errors.lastName}
                    placeholder="Last name"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.lastName}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>

            {/* Phone */}
            <Form.Group className="mb-3">
              <Form.Label>Phone Number *</Form.Label>
              <Form.Control
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                isInvalid={!!errors.phoneNumber}
                placeholder="(555) 123-4567"
                inputMode="tel"
                autoComplete="tel"
              />
              <Form.Control.Feedback type="invalid">
                {errors.phoneNumber}
              </Form.Control.Feedback>
            </Form.Group>

            {/* How do you identify yourself */}
            <Form.Group className="mb-3">
              <Form.Label>How do you identify yourself? *</Form.Label>
              <Form.Select
                name="identity"
                value={formData.identity}
                onChange={handleChange}
                isInvalid={!!errors.identity}
              >
                <option value="">Select...</option>
                <option value="parent">Parent/Guardian</option>
                <option value="caregiver">Caregiver</option>
                <option value="sibling">Sibling</option>
                <option value="family">Other Family Member</option>
                <option value="professional">Professional/Service Provider</option>
                <option value="self">Self-Advocate</option>
                <option value="other">Other</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.identity}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Zip Code */}
            <Form.Group className="mb-3">
              <Form.Label>Zip Code (optional)</Form.Label>
              <Form.Control
                type="text"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                isInvalid={!!errors.zipCode}
                placeholder="12345"
                inputMode="numeric"
                autoComplete="postal-code"
              />
              <Form.Control.Feedback type="invalid">
                {errors.zipCode}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Needs */}
            <div className="mb-3">
              <Form.Label className="d-block fw-semibold mb-1">
                What are your needs? <span className="text-muted fw-normal">(optional)</span>
              </Form.Label>
              <Form.Text className="text-muted d-block mb-2">
                Share up to three things you're looking for support with.
              </Form.Text>
              <Form.Control
                type="text"
                name="need1"
                value={formData.need1}
                onChange={handleChange}
                placeholder="Need 1 (e.g. Finding therapy services)"
                className="mb-2"
                maxLength={500}
              />
              <Form.Control
                type="text"
                name="need2"
                value={formData.need2}
                onChange={handleChange}
                placeholder="Need 2 (e.g. Understanding IEP options)"
                className="mb-2"
                maxLength={500}
              />
              <Form.Control
                type="text"
                name="need3"
                value={formData.need3}
                onChange={handleChange}
                placeholder="Need 3 (e.g. Connecting with other parents)"
                maxLength={500}
              />
            </div>

            {/* Profile Picture */}
            <Form.Group className="mb-3">
              <Form.Label>Profile Picture (optional)</Form.Label>
              <Form.Control
                type="file"
                name="profilePicture"
                accept="image/*"
                onChange={handleChange}
              />
              <Form.Text className="text-muted">
                Upload a profile picture (JPG, PNG, GIF)
              </Form.Text>
            </Form.Group>

            {/* Terms of Service */}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleChange}
                isInvalid={!!errors.acceptTerms}
                label={
                  <span>
                    I accept the{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>{' '}
                    *
                  </span>
                }
                feedback={errors.acceptTerms}
                feedbackType="invalid"
              />
            </Form.Group>

            {/* Newsletter */}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="newsletterOptIn"
                checked={formData.newsletterOptIn}
                onChange={handleChange}
                label="Subscribe to our newsletter for updates and resources"
              />
            </Form.Group>

            {/* SMS: separate, optional, unchecked by default (agreed 10-08). */}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="signup-sms-consent"
                name="smsConsent"
                checked={formData.smsConsent}
                onChange={handleChange}
                label={SMS_CONSENT_TEXT}
              />
            </Form.Group>

            {/* Placeholder for future custom fields */}
            <div className="mt-3 text-muted">
              <small>
                <em>Additional profile fields will be available in future updates</em>
              </small>
            </div>
          </>
        )}

        {/* Forgot Password (login mode only) */}
        {mode === 'login' && (
          <div className="d-flex justify-content-end mb-3">
            <Button variant="link" size="sm" onClick={handleForgotPassword} className="p-0">
              Forgot Password?
            </Button>
          </div>
        )}

        {/* Submit Button */}
        <Button
          variant="primary"
          type="submit"
          disabled={loading || uploadingPicture}
          className="w-100 mt-3"
          size="lg"
        >
          {uploadingPicture ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Uploading picture...
            </>
          ) : loading ? (
            'Please wait...'
          ) : (
            mode === 'login' ? 'Sign In' : 'Create Account'
          )}
        </Button>
      </Form>

      {/* Mode Switch */}
      <div className="text-center mt-4">
        <p className="mb-0">
          {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
          <Button variant="link" onClick={handleModeSwitch} className="p-0">
            {mode === 'login' ? 'Sign Up' : 'Sign In'}
          </Button>
        </p>
      </div>
    </div>
  );
};

export default AuthForm;
